import React, { useState } from 'react';
import { createPaperTradingSession } from '../api';
import Spinner from './Spinner';

export default function StartPaperTradingModal({ backtest, onStarted, onClose }) {
  const [step, setStep] = useState(1); // 1 = capital, 2 = confirm
  const [startingCapital, setStartingCapital] = useState(
    backtest?.results?.starting_value ? String(backtest.results.starting_value) : '10000'
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const capitalNum = parseFloat(startingCapital.replace(/,/g, '')) || 0;

  const handleStart = async () => {
    setLoading(true);
    setError(null);
    try {
      const today = new Date().toISOString().split('T')[0];
      const payload = {
        backtest: backtest?.id || null,
        strategy: backtest?.strategy || backtest?.strategy_id,
        name: `${backtest?.strategy_name || 'Strategy'} — ${backtest?.symbol || ''}`.trim(),
        symbol: backtest?.symbol,
        asset_type: backtest?.asset_type,
        starting_capital: capitalNum,
        commission_perc: backtest?.commission_perc ?? 0.1,
        slippage_perc: backtest?.slippage_perc ?? 0.1,
        fill_on_close: backtest?.fill_on_close ?? false,
        fx_interval: backtest?.fx_interval ?? '1d',
        started_at: today,
      };
      const { data: session } = await createPaperTradingSession(payload);
      // Note: we deliberately do NOT touch notification preferences here. New
      // users get sensible defaults at signup (trade + daily alerts on), and
      // forcing choices in this wizard used to silently overwrite prefs the user
      // had set elsewhere. Notifications are managed on the profile page.
      onStarted(session);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to start paper trading. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12,
        padding: 32, maxWidth: 520, width: '100%', position: 'relative',
      }}>
        <button onClick={onClose} style={{
          position: 'absolute', top: 16, right: 16, background: 'none', border: 'none',
          color: 'var(--muted)', fontSize: 18, cursor: 'pointer', lineHeight: 1,
        }}>✕</button>

        {/* Step indicators */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 28 }}>
          {['Capital', 'Confirm'].map((label, i) => (
            <div key={i} style={{ flex: 1, textAlign: 'center' }}>
              <div style={{
                height: 3, borderRadius: 2,
                background: i < step ? 'var(--teal)' : 'var(--border)',
                marginBottom: 6,
                transition: 'background 0.2s',
              }} />
              <span style={{ fontSize: 11, color: i + 1 === step ? 'var(--teal)' : 'var(--muted)' }}>{label}</span>
            </div>
          ))}
        </div>

        {/* Step 1: Capital */}
        {step === 1 && (
          <>
            <h3 style={{ margin: '0 0 6px', color: 'var(--text)', fontSize: 18 }}>Start Paper Trading</h3>
            <p style={{ color: 'var(--muted)', margin: '0 0 24px', fontSize: 13 }}>
              Simulate trading {backtest?.strategy_name} on {backtest?.symbol} with virtual capital.
            </p>

            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--muted)' }}>
              Starting Capital
            </label>
            <div style={{ position: 'relative', marginBottom: 24 }}>
              <span style={{
                position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
                color: 'var(--muted)', fontSize: 15,
              }}>$</span>
              <input
                type="number"
                min="100"
                value={startingCapital}
                onChange={(e) => setStartingCapital(e.target.value)}
                style={{
                  width: '100%', boxSizing: 'border-box', paddingLeft: 28, paddingRight: 12,
                  padding: '10px 12px 10px 28px', fontSize: 18, fontWeight: 700,
                  background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
                  color: 'var(--text)',
                }}
              />
            </div>

            {/* Summary box */}
            <div style={{
              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
              padding: 16, marginBottom: 24, fontSize: 13,
            }}>
              <Row label="Strategy" value={backtest?.strategy_name || '—'} />
              <Row label="Symbol" value={backtest?.symbol || '—'} />
              <Row label="Asset type" value={backtest?.asset_type || '—'} />
              <Row label="Commission" value={`${backtest?.commission_perc ?? 0.1}%`} />
              <Row label="Slippage" value={`${backtest?.slippage_perc ?? 0.1}%`} />
              <Row label="Starting capital" value={capitalNum > 0 ? `$${capitalNum.toLocaleString()}` : '—'} highlight />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={onClose} style={secondaryBtn}>Cancel</button>
              <button
                onClick={() => setStep(2)}
                disabled={capitalNum < 100}
                style={{ ...primaryBtn, opacity: capitalNum < 100 ? 0.5 : 1 }}
              >
                Next: Confirm →
              </button>
            </div>
          </>
        )}

        {/* Step 2: Confirm */}
        {step === 2 && (
          <>
            <h3 style={{ margin: '0 0 6px', color: 'var(--text)', fontSize: 18 }}>Ready to Launch</h3>
            <p style={{ color: 'var(--muted)', margin: '0 0 20px', fontSize: 13 }}>
              Confirm your paper trading setup. The strategy will run daily at 4:30 PM ET.
            </p>

            <div style={{
              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
              padding: 16, marginBottom: 16, fontSize: 13,
            }}>
              <Row label="Strategy" value={backtest?.strategy_name || '—'} />
              <Row label="Symbol" value={`${backtest?.symbol} (${backtest?.asset_type})`} />
              <Row label="Starting capital" value={`$${capitalNum.toLocaleString()}`} highlight />
              <Row label="Commission" value={`${backtest?.commission_perc ?? 0.1}%`} />
              <Row label="Slippage" value={`${backtest?.slippage_perc ?? 0.1}%`} />
            </div>

            <div style={{
              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
              padding: 14, marginBottom: 24, fontSize: 12, color: 'var(--muted)', lineHeight: 1.6,
            }}>
              🔔 We'll email you when this strategy opens or closes a position. You can change
              email preferences anytime on your profile.
            </div>

            {error && (
              <div style={{
                background: 'rgba(248,81,73,0.12)', border: '1px solid rgba(248,81,73,0.4)',
                borderRadius: 6, padding: '10px 14px', marginBottom: 16, color: '#f85149', fontSize: 13,
              }}>{error}</div>
            )}

            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setStep(1)} style={secondaryBtn} disabled={loading}>← Back</button>
              <button onClick={handleStart} disabled={loading} style={primaryBtn}>
                {loading ? <Spinner size={16} /> : '🚀 Launch Paper Trading'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, highlight }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
      <span style={{ color: 'var(--muted)' }}>{label}</span>
      <span style={{ color: highlight ? 'var(--teal)' : 'var(--text)', fontWeight: highlight ? 700 : 400 }}>
        {value}
      </span>
    </div>
  );
}

const primaryBtn = {
  flex: 1, background: 'var(--teal)', color: 'var(--bg)', border: 'none',
  padding: '10px 16px', borderRadius: 8, fontSize: 14, fontWeight: 700,
  cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
};

const secondaryBtn = {
  background: 'var(--surface)', color: 'var(--text)', border: '1px solid var(--border)',
  padding: '10px 16px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
};
